import { Component, Inject, Injector, OnInit, NgZone, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { BaseSurveyStepComponent } from '../base-survey-step/base-survey-step.component';
import { AddressModel, CountryModel, StateModel, CityModel } from '@nx-c4g/c4g-models';
import { MapsLoaderService } from '../../google-places/maps-loader.service';
import { SurveyResponse } from '@nx-c4g/c4g-models';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CountriesService } from '../../google-places/countries.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'c4g-address-survey-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatAutocompleteModule, TranslateModule],
  templateUrl: './address-survey-step.component.html',
  styleUrls: ['./address-survey-step.component.scss']
})
export class AddressSurveyStepComponent extends BaseSurveyStepComponent implements OnInit, AfterViewInit {
  addressInput = '';
  suggestions: any[] = [];
  selectedAddress: AddressModel | null = null;
  manualMode = false;
  manualAddress: AddressModel = new AddressModel();
  loading = false;
  countries: CountryModel[] = [];
  states: StateModel[] = [];
  cities: CityModel[] = [];
  selectedCountry: CountryModel | null = null;
  selectedState: StateModel | null = null;
  selectedCity: CityModel | null = null;
  stateSearch = '';
  citySearch = '';
  filteredStates: StateModel[] = [];
  filteredCities: CityModel[] = [];
  private isRestoring = false;
  @ViewChild('addressInputEl') addressInputEl?: ElementRef<HTMLInputElement>;

  constructor(
    @Inject('stepData') injectedStepData: any,
    @Inject('stepResponse') stepResponseFn: (response: SurveyResponse) => void,
    @Inject('initialStepResponse') initialStepResponse: SurveyResponse,
    private mapsLoader: MapsLoaderService,
    private countriesService: CountriesService,
    private ngZone: NgZone,
    private translate: TranslateService
  ) {
    super(injectedStepData, stepResponseFn, initialStepResponse);
  }

  override async ngOnInit() {
    await this.mapsLoader.load();
    super.ngOnInit();
    this.countries = await this.countriesService.getCountries();
    // Default to Canada if not already set, and only if no address is present
    const manualAddressEmpty = !this.manualAddress.Street && !this.manualAddress.City && !this.manualAddress.State && !this.manualAddress.PostalCode && !this.manualAddress.Country;
    console.log('[ngOnInit] selectedCountry:', this.selectedCountry, 'selectedAddress:', this.selectedAddress, 'manualAddressEmpty:', manualAddressEmpty);
    if (!this.selectedCountry && !this.selectedAddress && manualAddressEmpty) {
      const canada = this.countries.find(c => c.name.toLowerCase() === 'canada' || c.iso2 === 'CA');
      if (canada) {
        console.log('[ngOnInit] Defaulting to Canada');
        this.selectedCountry = canada;
        this.onCountrySelect(canada);
      }
    }
    // Restore previous response if available
    if (this.initialStepResponse && this.initialStepResponse.response?.[0]?.values?.[0]) {
      this.isRestoring = true;
      try {
        const address = JSON.parse(this.initialStepResponse.response[0].values[0]);
        // If address is 'clean', treat as selectedAddress (suggestion mode)
        if (address.Clean) {
          this.selectedAddress = address;
          this.manualMode = false;
          this.addressInput = this.formatAddress(address);
        } else {
          // Otherwise, treat as manual address
          this.manualAddress = address;
          this.manualMode = true;
          // Set country, state, city for manual mode
          if (address.countryCode || address.Country) {
            const country = this.countries.find(c => c.iso2 === address.countryCode || c.name === address.Country);
            if (country) {
              this.selectedCountry = country;
              this.onCountrySelect(country);
            }
          }
          if (address.stateOrProvinceCode || address.State) {
            this.states = this.selectedCountry?.states || [];
            const state = this.states.find(s => s.state_code === address.stateOrProvinceCode || s.name === address.State);
            if (state) {
              this.selectedState = state;
              this.stateSearch = state.name;
              this.onStateSelect(state);
            }
          }
          if (address.City) {
            this.cities = this.selectedState?.cities || [];
            const city = this.cities.find(c => c.name === address.City);
            if (city) {
              this.selectedCity = city;
              this.citySearch = city.name;
              this.onCitySelect(city);
            }
          }
        }
      } catch (e) {
        // Ignore parse errors
      }
      this.isRestoring = false;
    }
  }

  async onAddressInputChange(value?: string) {
    this.suggestions = []; // Clear previous results before searching
    const inputValue = value !== undefined ? value : this.addressInput;
    console.log('[onAddressInputChange] value param:', value, 'inputValue used:', inputValue);
    if (!inputValue) {
      return;
    }
    this.loading = true;
    await this.mapsLoader.load();
    // Check if google.maps and google.maps.places are available
    if (!(window as any).google || !(window as any).google.maps || !(window as any).google.maps.places) {
      this.loading = false;
      // Optionally show an error message to the user
      console.log('[onAddressInputChange] Google Maps API not available');
      return;
    }
    const request = {
      query: inputValue,
      fields: ['formatted_address', 'geometry', 'place_id'],
    };
    console.log('[onAddressInputChange] Sending request:', request);
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.findPlaceFromQuery(request, (results: any[], status: any) => {
      console.log('[onAddressInputChange] PlacesService callback status:', status, 'results:', results);
      this.ngZone.run(() => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          this.suggestions = results.slice(0, 3);
        } else {
          this.suggestions = [];
        }
        this.loading = false;
      });
    });
  }

  async selectSuggestion(suggestion: any) {
    await this.mapsLoader.load();
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.place_id }, async (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        // Use invokeEvent logic from GooglePlacesComponent
        const address = await this.invokeEvent(results[0]);
        this.selectedAddress = address;
        this.manualMode = false;
        this.suggestions = [];
        this.addressInput = this.formatAddress(address);
      }
    });
  }

  async invokeEvent(place: google.maps.GeocoderResult): Promise<AddressModel> {
    // Extract address fields as in GooglePlacesComponent
    const address: AddressModel = new AddressModel();
    for (const component of place.address_components) {
      if (component.types.includes('street_number')) {
        address.Street = component.long_name;
      } else if (component.types.includes('route')) {
        address.Street = (address.Street ? address.Street + ' ' : '') + component.long_name;
      } else if (component.types.includes('locality')) {
        address.City = component.long_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        address.State = component.long_name;
        address.stateOrProvinceCode = component.short_name;
      } else if (component.types.includes('postal_code')) {
        address.PostalCode = component.long_name;
      } else if (component.types.includes('country')) {
        address.Country = component.long_name;
        address.countryCode = component.short_name;
      }
    }
    address.Latitude = place.geometry.location.lat();
    address.Longitude = place.geometry.location.lng();
    address.CityStatePostalCode = `${address.City}, ${address.State} ${address.PostalCode}`;
    address.Clean = true;
    address.Type = '';
    address.timeZone = await this.mapsLoader.timeZoneByCoordinates(address.Latitude, address.Longitude);
    return address;
  }

  formatAddress(address: AddressModel): string {
    return `${address.Street}, ${address.City}, ${address.State}, ${address.Country}, ${address.PostalCode}`;
  }

  useManualMode() {
    this.manualMode = true;
    this.suggestions = [];
    // Only require core address fields to be empty for prefill
    const coreFieldsEmpty = !this.manualAddress.Street && !this.manualAddress.City && !this.manualAddress.State && !this.manualAddress.PostalCode;
    if (this.selectedAddress && coreFieldsEmpty) {
      const addr = this.selectedAddress;
      console.log('[useManualMode] Prefilling manualAddress from selectedAddress:', addr);
      this.manualAddress = { ...addr };
      // Set country, state, city dropdowns
      if (addr.countryCode || addr.Country) {
        const country = this.countries.find(c => c.iso2 === addr.countryCode || c.name === addr.Country);
        if (country) {
          this.selectedCountry = country;
          this.onCountrySelect(country);
        }
      }
      if (addr.stateOrProvinceCode || addr.State) {
        this.states = this.selectedCountry?.states || [];
        const state = this.states.find(s => s.state_code === addr.stateOrProvinceCode || s.name === addr.State);
        if (state) {
          this.selectedState = state;
          this.stateSearch = state.name;
          this.onStateSelect(state);
        }
      }
      if (addr.City) {
        this.cities = this.selectedState?.cities || [];
        const city = this.cities.find(c => c.name === addr.City);
        if (city) {
          this.selectedCity = city;
          this.citySearch = city.name;
          this.onCitySelect(city);
        }
      }
    } else {
      console.log('[useManualMode] Not prefilling manualAddress. selectedAddress:', this.selectedAddress, 'manualAddress:', this.manualAddress);
    }
    this.selectedAddress = null;
  }

  onCountrySelect(country: CountryModel) {
    this.selectedCountry = country;
    this.states = country.states || [];
    this.selectedState = null;
    this.cities = [];
    this.selectedCity = null;
    this.manualAddress.Country = country.name;
    this.manualAddress.countryCode = country.iso2;
  }

  onStateSearchChange(value: string) {
    this.stateSearch = value;
    this.filteredStates = this.states.filter(state => {
      if (typeof value === 'string') {
        return state.name.toLowerCase().includes(value.toLowerCase());
      }
      return false;
    });
  }

  onCitySearchChange(value: string) {
    this.citySearch = value;
    this.filteredCities = this.cities.filter(city => {
      if (typeof value === 'string') {
        return city.name.toLowerCase().includes(value.toLowerCase());
      }
      return false;
    });
  }

  onStateSelect(state: StateModel) {
    this.selectedState = state;
    this.stateSearch = state.name;
    this.cities = state.cities || [];
    this.selectedCity = null;
    this.manualAddress.State = state.name;
    this.manualAddress.stateOrProvinceCode = state.state_code;
    this.citySearch = '';
    this.filteredCities = this.cities;
  }

  onCitySelect(city: CityModel) {
    this.selectedCity = city;
    this.citySearch = city.name;
    this.manualAddress.City = city.name;
    if (city.latitude && city.longitude) {
      this.manualAddress.Latitude = city.latitude;
      this.manualAddress.Longitude = city.longitude;
    }
  }

  onManualFieldChange() {
  }

  /**
   * Helper to geocode the manual address fields and update manualAddress with lat/lng
   */
  private async geocodeManualAddress(): Promise<void> {
    // Use MapsLoaderService to clean and geocode the address
    const cleaned = await this.mapsLoader.cleanAddressWithGoogleMaps(this.manualAddress);
    this.manualAddress = cleaned;
  }

  // Synchronous getResponse for compatibility with base class
  override async getResponse(): Promise<SurveyResponse | undefined> {
    if (!this.stepData) return undefined;
    if (this.manualMode) {
      await this.geocodeManualAddress();
      return {
        stepId: this.stepData._id,
        showValueInReport: true,
        response: [{ _id: 'addressRecord', values: [JSON.stringify(this.manualAddress)] }]
      };
    } else {
      const address = this.selectedAddress;
      if (!address) return undefined;
      return {
        stepId: this.stepData._id,
        showValueInReport: true,
        response: [{ _id: 'addressRecord', values: [JSON.stringify(address)] }]
      };
    }
  }

  override isValid(): boolean {
    if (this.manualMode) {
      // Basic validation for manual fields
      return !!(this.manualAddress.Street && this.manualAddress.City && this.manualAddress.State && this.manualAddress.PostalCode && this.manualAddress.Country);
    }
    return !!this.selectedAddress;
  }

  getLangValue(mls: { en: string; fr: string }): string {
    const lang = this.translate.currentLang === 'fr' ? 'fr' : 'en';
    return mls?.[lang] || mls?.['en'] || '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.manualMode && this.addressInputEl) {
        this.addressInputEl.nativeElement.focus();
      }
    });
  }
}
